/**
 * The way in.
 *
 * Two fields and one button. Signing in happens about once a month per person
 * — the session lasts thirty days — so this screen's job is not to be
 * efficient for repeat use, it is to be unambiguous for somebody who has not
 * seen it since last month and is holding a phone on a shoot.
 *
 * There is still no self-serve reset, and there cannot be: nobody here has an
 * email address on file, so there is nowhere to send a link. What there is
 * instead is a way to be noticed. The screen used to say "ask a partner",
 * which is fine at a desk and useless at nine at night — so the sentence is
 * now a button that puts your name on the partner's roster as somebody who
 * cannot get in.
 *
 * It answers the same way whatever you type, because an endpoint open to the
 * internet that says "no such person" is a way to ask who works here.
 *
 * Above the two fields there is now a Google button, when the deployment has
 * Google configured and Google's script loads. It is above rather than below
 * because it is the faster of the two for anybody it works for, and it is
 * conditional the whole way down: no configuration, no script, no network —
 * no button, no error, and the password field exactly where it was. A second
 * way in that breaks the first one is worse than no second way in.
 *
 * It is not a way to join. A Google account signs in only if a partner has
 * already typed its address onto an existing roster row.
 *
 * It sits outside `AppShell` but on the same dark canvas, so the first thing
 * anybody sees is the surface the rest of the platform is built on.
 */

import { useCallback, useEffect, useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGoogleSignIn } from '../hooks/useGoogleSignIn';
import { api, ApiError } from '../api';
import { Aurora } from '../ui/Aurora';
import { Button } from '../components/Button';
import { Field } from '../ui/Field';

export function SignIn() {
  const { login, refresh, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [asked, setAsked] = useState<string | null>(null);

  // The navigate happens in the effect below, on `isAuthenticated`, so this
  // only has to make the session known.
  const google = useGoogleSignIn(refresh);

  /**
   * Put a hand up.
   *
   * Deliberately not told whether it worked, because the server does not know
   * either — it answers the same for a name on the roster and a name that has
   * never existed. A network failure is the one thing worth reporting, and
   * even that is reported as "try again" rather than as an error, because the
   * person reading it is already having a bad time.
   */
  const raiseHand = useCallback(async () => {
    if (asking) return;
    setAsking(true);
    try {
      const said = await api<{ message: string }>('/session/help', {
        method: 'POST',
        body: { id: id.trim().toLowerCase() },
        unversioned: true
      });
      setAsked(said.message);
    } catch {
      setAsked('Could not reach the server. Check your connection and try again.');
    } finally {
      setAsking(false);
    }
  }, [asking, id]);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (busy) return;

      setBusy(true);
      setError(null);
      try {
        await login(id, password);
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : 'Could not reach the server. Check your connection.'
        );
        // The name stays, the password goes. Retyping a name you have already
        // typed correctly is the small indignity that makes people give up.
        setPassword('');
      } finally {
        setBusy(false);
      }
    },
    [busy, id, password, login]
  );

  // Navigating on the state rather than after the call, so that arriving here
  // with a session already live — a bookmark, a back button, a second tab —
  // bounces straight through instead of showing a password box to somebody who
  // is signed in.
  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  return (
    <Frame>
      <form onSubmit={submit} className="flex flex-col gap-3.5">
        <header className="mb-1">
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-950">
            Sign in
          </h1>
          <p className="mt-1 text-sm text-ink-500">Pick up where the studio is.</p>
        </header>

        {google.configured && (
          <>
            {/* Present as soon as Google is configured, because Google draws
                into it — but flat until it has, so a blocked script leaves no
                empty gap above the password field. Collapsed rather than
                `display: none`, which measures as zero-width and produces a
                button to match. */}
            <div
              className={
                google.offered ? 'flex justify-center' : 'h-0 overflow-hidden opacity-0'
              }
            >
              <div ref={google.ref} />
            </div>

            {google.error !== null && <Problem message={google.error} />}

            {google.offered && (
              // Named rather than a bare rule: two ways in, and the second one
              // still works if the first does nothing.
              <div className="flex items-center gap-3 py-0.5">
                <span className="h-px flex-1 bg-ink-100" />
                <span className="text-[11px] uppercase tracking-wide text-ink-400">
                  or with a password
                </span>
                <span className="h-px flex-1 bg-ink-100" />
              </div>
            )}
          </>
        )}

        <Field
          label="Email or name"
          id="signin-id"
          name="username"
          value={id}
          onChange={(e) => setId(e.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="next"
          required
          // Either works, and the server matches both in one query: an id
          // is a slug and cannot contain an `@`, an address must, so there
          // is no ambiguity to resolve. Both are here rather than only the
          // address because the first partner is created before anybody has
          // one — see `src/db/bootstrap.ts`.
          placeholder="you@studiohappens.com"
        />

        <Field
          label="Password"
          id="signin-password"
          name="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          enterKeyHint="go"
          required
        />

        <Problem message={error} />

        <Button
          type="submit"
          size="lg"
          className="mt-1 w-full"
          disabled={busy || google.busy || id === '' || password === ''}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>

        {asked === null ? (
          <p className="text-xs leading-relaxed text-ink-500">
            Forgotten it? Hapi OS does not send email, so nothing can be sent to you
            automatically.{' '}
            <button
              type="button"
              disabled={id.trim() === '' || asking}
              onClick={() => {
                void raiseHand();
              }}
              className="font-medium text-brand-400 underline underline-offset-2 hover:text-brand-300 disabled:cursor-default disabled:text-ink-500 disabled:no-underline"
            >
              {asking ? 'Telling them…' : 'Tell a partner you are locked out'}
            </button>
            {id.trim() === '' && ' — fill in the box above first.'}
          </p>
        ) : (
          <p className="text-xs leading-relaxed text-ink-400">{asked}</p>
        )}
      </form>
    </Frame>
  );
}

/**
 * Choosing a password from a setup link.
 *
 * Its own screen rather than a mode of the one above, because everything
 * differs: there is no name to type, the person may never have seen the
 * platform, and the failure that matters is a link that has already been used.
 */
export function SetPassword({ token }: { token: string }) {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [who, setWho] = useState<string | null | 'loading'>('loading');
  // Why it did not work, in words from the server rather than a guess
  // by this screen. The guess used to be "expired", for all three
  // reasons, and it was wrong about the one that actually happened.
  const [whyNot, setWhyNot] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [asked, setAsked] = useState<string | null>(null);

  useEffect(() => {
    // `live` rather than an AbortController: the answer is one small row and
    // the request is cheap, but setting state after the token has changed
    // would greet somebody by the wrong name.
    let live = true;
    void api<{ id: string }>(`/session/setup/${encodeURIComponent(token)}`, { unversioned: true })
      .then((r) => {
        if (live) setWho(r.id);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setWhyNot(
          err instanceof ApiError
            ? err.message
            : 'Could not reach the server. Check your connection.'
        );
        setWho(null);
      });
    return () => {
      live = false;
    };
  }, [token]);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (busy) return;

      if (password !== again) {
        // Checked here rather than server-side: the server has no business
        // seeing a password twice, and the answer is instant either way.
        setError('Those two do not match.');
        return;
      }

      setBusy(true);
      setError(null);
      try {
        await api('/session/setup', { method: 'POST', body: { token, password }, unversioned: true });
        await refresh();
        // The token leaves the address bar the moment it is spent. It is a
        // credential, and address bars get copied, screenshotted and synced.
        navigate('/', { replace: true });
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : 'Could not reach the server. Check your connection.'
        );
      } finally {
        setBusy(false);
      }
    },
    [busy, password, again, token, refresh, navigate]
  );

  if (who === 'loading') {
    return (
      <Frame>
        <div className="flex flex-col gap-3">
          <div className="h-7 w-2/3 animate-pulse rounded-lg bg-ink-100" />
          <div className="h-4 w-full animate-pulse rounded bg-ink-100" />
          <div className="mt-2 h-11 w-full animate-pulse rounded-xl bg-ink-100" />
          <div className="h-11 w-full animate-pulse rounded-xl bg-ink-100" />
        </div>
      </Frame>
    );
  }

  if (who === null) {
    return (
      <Frame>
        {/* Not "expired". Three things bring somebody here and only one of
            them is a clock; the commonest is a link that lost a character on
            its way out of a terminal and into a chat window. The heading is
            the one sentence true of all three, and the server says which. */}
        <h1 className="font-display text-2xl font-bold tracking-tight text-ink-950">
          That link did not work
        </h1>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-500">
          {whyNot ?? 'Ask a partner to send you another.'}
        </p>
      </Frame>
    );
  }

  return (
    <Frame>
      <form onSubmit={submit} className="flex flex-col gap-3.5">
        <header className="mb-1">
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-950">
            Hello, {who}
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Choose a password. Nobody else will ever know it, including us.
          </p>
        </header>

        {/* Present but hidden: a password manager that cannot see which account
            this is will offer to save it under the wrong one, or not at all. */}
        <input type="text" name="username" value={who} readOnly hidden autoComplete="username" />

        <Field
          label="Password"
          hint="At least 12 characters. A short sentence you will remember beats something clever you will not."
          id="new-password"
          name="new-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          enterKeyHint="next"
          required
        />

        <Field
          label="And again"
          id="again-password"
          name="again-password"
          type="password"
          value={again}
          onChange={(e) => setAgain(e.target.value)}
          autoComplete="new-password"
          enterKeyHint="go"
          required
        />

        <Problem message={error} />

        <Button
          type="submit"
          size="lg"
          className="mt-1 w-full"
          disabled={busy || password === '' || again === ''}
        >
          {busy ? 'Setting it…' : 'Set password and sign in'}
        </Button>
      </form>
    </Frame>
  );
}

/**
 * One white card on the dark canvas, centred, capped at a comfortable reading
 * width. `min-h-dvh` rather than `min-h-screen`: on iOS `100vh` is the height
 * the page would have if the browser chrome retracted, so a form centred in it
 * sits partly under the address bar until somebody scrolls.
 */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <main className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-[#101014] px-4 py-10">
      <Aurora id="signin-grain" />

      <div className="relative z-10 w-full max-w-[26rem]">
        <div className="mb-5 flex items-center justify-center gap-2.5">
          <BrandMark />
          <span className="font-display text-[15px] font-bold tracking-tight text-white">
            Hapi OS
          </span>
        </div>

        <div className="rounded-card border border-white/10 bg-white p-6 shadow-pop sm:p-7">
          {children}
        </div>
      </div>
    </main>
  );
}

function Problem({ message }: { message: string | null }) {
  if (message === null) return null;
  return (
    <p
      role="alert"
      className="rounded-xl border border-danger-500/20 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-600"
    >
      {message}
    </p>
  );
}
